import { Search, X } from 'lucide-react';
import { FilterChips, FilterSelect } from '@/components/patterns/filter-bar';
import { ChipPeriodo, FiltroPeriodo } from '@/components/patterns/filtro-periodo';
import { StatusIcone } from '@/components/patterns/status-badge';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/field';
import { Switch } from '@/components/ui/switch';
import { PRODUTOS, STATUS_BUILD } from '@/data';
import { ROTULO_STATUS } from '@/lib/capabilities';
import type { OpcoesFiltros } from './PainelFiltros';
import type { FiltrosFeedEstado } from './use-filtros-feed';

/**
 * Barra de filtros HORIZONTAL no topo da vista "Feed": busca, produto, ramo,
 * branch, período, status e "Com incidente" (só com acesso a Incidentes), em
 * linhas que quebram sem cortar nada. Sem lateral.
 */
export function BarraFiltros({ f, opcoes }: { f: FiltrosFeedEstado; opcoes: OpcoesFiltros }) {
  const comRamo = opcoes.mostrarRamo && !opcoes.ramoFixo;
  return (
    <section aria-label="Filtros" className="flex flex-col gap-3 rounded-card border border-border bg-bg-surface p-4 shadow-sm">
      <div role="search" aria-label="Filtros do feed" className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <Field id="barra-busca" label="Buscar versão" className="min-w-56 flex-1 basis-64">
          {(props) => (
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-icon" aria-hidden="true" />
              <Input
                {...props}
                type="search"
                value={f.busca}
                placeholder="2.2602.78 ou 4510"
                onChange={(evento) => f.setBusca(evento.target.value)}
                className="pl-9"
              />
            </div>
          )}
        </Field>
        <FilterSelect
          id="barra-produto"
          label="Produto"
          valor={f.produto}
          aoMudar={(valor) => f.atualizar({ produto: valor })}
          opcoes={Object.entries(PRODUTOS).map(([valor, rotulo]) => ({ valor, rotulo }))}
          className="w-44 min-w-0"
        />
        {comRamo ? (
          <FilterSelect
            id="barra-ramo"
            label="Ramo"
            valor={f.ramo}
            aoMudar={(valor) => f.atualizar({ ramo: valor, branch: null })}
            opcoes={opcoes.opcoesRamo}
            className="w-32 min-w-0"
          />
        ) : null}
        <FilterSelect
          id="barra-branch"
          label="Branch"
          valor={f.branch}
          aoMudar={(valor) => f.atualizar({ branch: valor })}
          opcoes={opcoes.opcoesBranch}
          rotuloTodos="Todas"
          className="w-36 min-w-0"
        />
        <FiltroPeriodo
          id="barra-periodo"
          de={f.periodo.de}
          ate={f.periodo.ate}
          aoMudar={(p) => f.atualizar({ de: p.de, ate: p.ate })}
        />
        <FilterChips
          id="barra-status"
          label="Status"
          valores={f.status}
          aoMudar={(valores) => f.atualizar({ status: valores })}
          className="shrink-0"
          opcoes={STATUS_BUILD.map((valor) => ({ valor, rotulo: ROTULO_STATUS[valor], icone: <StatusIcone status={valor} /> }))}
        />
        {f.podeIncidentes ? (
          <label className="flex h-10 cursor-pointer items-center gap-2 text-label" htmlFor="barra-incidente">
            <Switch id="barra-incidente" checked={f.comIncidente} onCheckedChange={(valor) => f.atualizar({ com_incidente: valor ? 'true' : null })} />
            Com incidente
          </label>
        ) : null}
      </div>
      {f.quantidade > 0 ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-border-subtle pt-3">
          <ChipPeriodo de={f.periodo.de} ate={f.periodo.ate} aoLimpar={() => f.atualizar({ de: null, ate: null })} />
          <Button variant="ghost" size="sm" onClick={f.limpar} className="ml-auto">
            <X aria-hidden="true" />
            Limpar {f.quantidade === 1 ? '1 filtro' : `${f.quantidade} filtros`}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
